import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../core/services/api/auth.service';

const PUBLIC_URLS = ['/auth/login', '/auth/nfc-login', '/auth/register', '/users/register', '/auth/refresh'];
const TOKEN_NEAR_EXPIRY_MS = 5 * 60 * 1000;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);

    const isPublicUrl = PUBLIC_URLS.some((url) => req.url.includes(url));
    if (isPublicUrl) return next(req);

    const token = authService.getAccessToken();
    if (!token) return next(req);

    if (authService.isTokenNearExpiry(token, TOKEN_NEAR_EXPIRY_MS)) {
        return authService.refreshToken().pipe(
            switchMap(() => {
                const newToken = authService.getAccessToken();
                if (!newToken) return next(req);
                const clonedReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${newToken}` }
                });
                return next(clonedReq);
            }),
            catchError(() => {
                const clonedReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${token}` }
                });
                return next(clonedReq);
            })
        );
    }

    const clonedReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
    });

    return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !req.url.includes('/auth/refresh')) {
                return authService.refreshToken().pipe(
                    switchMap(() => {
                        const newToken = authService.getAccessToken();
                        const retryReq = req.clone({
                            setHeaders: { Authorization: `Bearer ${newToken}` }
                        });
                        return next(retryReq);
                    }),
                    catchError((refreshError) => {
                        return throwError(() => refreshError);
                    })
                );
            }
            return throwError(() => error);
        })
    );
};
